import { useEffect, useMemo, useState } from "react";
import { api } from "../../../api";
import type { Client, Professional } from "../../../types";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { useLanguage } from "../../../contexts/LanguageContext";
import { apptLabels } from "../labels";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet";
import { parseMoneyToCents } from "../../utils/money";
import { Label } from "../../../components/ui/label";
import { ClientProfessionalSection } from "./ClientProfessionalSection";
import { DateTimeSection } from "./DateTimeSection";
import { RecurrenceSection } from "./RecurrenceSection";
import { PaymentSection } from "./PaymentSection";
import { NotesSection } from "./NotesSection";

type RecurringSlot = {
  id: string;
  weekday: number;
  startTime: string;
  endTime: string;
};

const PRICING_RULES: Record<string, Record<number, number>> = {
  "default": {
    1: 19800,
    2: 33000,
    3: 45000,
    4: 55000,
  }
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialDate?: Date;
  initialTime?: string;
  initialEmployeeId?: number | null;
}

export function AppointmentCreateDrawer({ open, onClose, onSuccess, initialDate, initialTime, initialEmployeeId }: Props) {
  const { lang } = useLanguage();
  const t = apptLabels[lang];

  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [specialties, setSpecialties] = useState<Array<{ id: number; name: string; room_id?: number | null }>>([]);
  const [insuranceProviders, setInsuranceProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Form State
  const [clientId, setClientId] = useState<number | null>(null);
  const [employeeId, setEmployeeId] = useState<number | null>(initialEmployeeId ?? null);
  const [specialtyId, setSpecialtyId] = useState<number | null>(null);
  const [insuranceProviderId, setInsuranceProviderId] = useState<number | null>(null);
  const [roomId, setRoomId] = useState<number | null>(null);
  const [date, setDate] = useState<Date>(() => initialDate || new Date());
  const [startTime, setStartTime] = useState<string>(initialTime || "08:00");
  const [endTime, setEndTime] = useState<string>("");
  const [notes, setNotes] = useState("");

  // Recurrence State
  const [isRecurring, setIsRecurring] = useState(false);
  const [endType, setEndType] = useState<'never' | 'weeks'>('never');
  const [recurrenceWeeks, setRecurrenceWeeks] = useState(4);
  const [recurringSlots, setRecurringSlots] = useState<RecurringSlot[]>([
    { id: Math.random().toString(36).substr(2, 9), weekday: new Date().getDay(), startTime: "08:00", endTime: "09:00" }
  ]);

  // Payment State
  const [priceText, setPriceText] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid'>('pending');
  const [billingDay, setBillingDay] = useState(5);
  const [isProportional, setIsProportional] = useState(false);
  const [firstPaymentText, setFirstPaymentText] = useState("");
  const [firstPaymentDate, setFirstPaymentDate] = useState<Date>(() => new Date());
  const [proportionalType, setProportionalType] = useState<'amount' | 'percentage'>('percentage');
  const [proportionalPercentage, setProportionalPercentage] = useState<string>("50");

  const insuranceOptions = useMemo(
    () => insuranceProviders.map((ip) => ({ value: String(ip.id), label: ip.name })),
    [insuranceProviders]
  );
  const isInsurance = useMemo(() => {
    if (!insuranceProviderId) return false;
    const provider = insuranceProviders.find(p => p.id === insuranceProviderId);
    return provider && !provider.is_default;
  }, [insuranceProviderId, insuranceProviders]);

  useEffect(() => {
    if (open) {
      if (!initialDate) {
        setDate(new Date());
      }
      setNotes("");
      setClientId(null);
      
      (async () => {
        const [profs, cls, specs, ins] = await Promise.all([
          api.listProfessionals(),
          api.listClients(),
          api.listCompanySpecialties(),
          api.listInsuranceProviders(),
        ]);
        if (profs.ok) setProfessionals(profs.data || []);
        if (cls.ok) setClients(cls.data || []);
        if (specs.ok) setSpecialties(specs.data || []);
        if (ins.ok) {
          setInsuranceProviders(ins.data || []);
          const def = ins.data.find((p: any) => p.is_default);
          if (def) setInsuranceProviderId(def.id);
        }
      })();
    }
  }, [open, initialDate]);

  useEffect(() => {
    if (isRecurring && isProportional && priceText && date) {
      const fullPrice = parseMoneyToCents(priceText);
      
      if (proportionalType === 'percentage') {
        const perc = parseFloat(proportionalPercentage.replace(',', '.')) || 0;
        const value = Math.round((fullPrice * perc) / 100);
        setFirstPaymentText((value / 100).toFixed(2).replace('.', ','));
      } else {
        const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const remainingDays = daysInMonth - date.getDate() + 1;
        const proportional = Math.round((fullPrice / daysInMonth) * remainingDays);
        setFirstPaymentText((proportional / 100).toFixed(2).replace('.', ','));
      }
      setFirstPaymentDate(new Date(date));
    }
  }, [isRecurring, isProportional, priceText, date, proportionalType, proportionalPercentage]);

  // Dynamic Pricing Logic
  useEffect(() => {
    if (isRecurring && specialtyId) {
      let rules = PRICING_RULES["default"];
      const savedPrices = localStorage.getItem("specialty_pricing");
      if (savedPrices) {
        try {
          const allPrices = JSON.parse(savedPrices);
          const specPrices = allPrices[String(specialtyId)];
          if (specPrices) {
            const parsedRules: Record<number, number> = {};
            [1, 2, 3, 4].forEach(f => {
              if (specPrices[f]) {
                const cents = Math.round(parseFloat(specPrices[f].replace(',', '.')) * 100);
                if (!isNaN(cents)) parsedRules[f] = cents;
              }
            });
            if (Object.keys(parsedRules).length > 0) rules = parsedRules;
          }
        } catch (e) {
          console.error("Error parsing saved prices", e);
        }
      }

      const count = recurringSlots.length;
      const priceCents = rules[count] || (rules[1] * count);
      setPriceText((priceCents / 100).toFixed(2).replace('.', ','));
    }
  }, [isRecurring, specialtyId, recurringSlots.length]);

  useEffect(() => {
    if (startTime && !endTime) {
      const [h, m] = startTime.split(":").map(Number);
      const end = new Date();
      end.setHours(h, m + 60, 0, 0);
      setEndTime(`${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`);
    }
  }, [startTime]);

  const canSave = useMemo(() => {
    if (!clientId || !employeeId || !specialtyId || !startTime || !endTime || !date) return false;
    const timeParts = startTime.split(":").map((v: string) => v.padStart(2, '0'));
    const endParts = endTime.split(":").map((v: string) => v.padStart(2, '0'));

    const sh = parseInt(timeParts[0], 10);
    const sm = parseInt(timeParts[1], 10);
    const eh = parseInt(endParts[0], 10);
    const em = parseInt(endParts[1], 10);
    const timeValid = (eh * 60 + em) > (sh * 60 + sm);
    if (isInsurance) return !!(clientId && employeeId && specialtyId && date && timeValid);
    return !!(clientId && employeeId && specialtyId && startTime && endTime && date && timeValid);
  }, [clientId, employeeId, specialtyId, startTime, endTime, date, isInsurance]);

  const addSlot = () => {
    setRecurringSlots([
      ...recurringSlots,
      { id: Math.random().toString(36).substr(2, 9), weekday: 1, startTime: "08:00", endTime: "09:00" }
    ]);
  };

  const removeSlot = (id: string) => {
    if (recurringSlots.length > 1) {
      setRecurringSlots(recurringSlots.filter(s => s.id !== id));
    }
  };

  const updateSlot = (id: string, field: keyof RecurringSlot, value: any) => {
    setRecurringSlots(recurringSlots.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);

    try {
      const pad2 = (n: number) => String(n).padStart(2, '0');
      const dateStr = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
      const startDT = `${dateStr} ${startTime}:00`;
      const sh = parseInt(startTime.split(':')[0], 10);
      const sm = parseInt(startTime.split(':')[1] || '00', 10);
      const eh = parseInt(endTime.split(':')[0], 10);
      const em = parseInt(endTime.split(':')[1] || '00', 10);
      const duration = (eh * 60 + em) - (sh * 60 + sm);

      if (isRecurring) {
        const res = await api.createRecurringPlan({
          client_id: Number(clientId),
          employee_id: Number(employeeId),
          room_id: roomId,
          specialty_id: specialtyId,
          insurance_provider_id: insuranceProviderId,
          name: notes.trim() || (notes ? notes.trim() : undefined),
          price_cents: isInsurance ? 0 : parseMoneyToCents(priceText),
          billing_period: 'monthly',
          billing_day: billingDay,
          schedules: recurringSlots.map(slot => {
            const d = new Date();
            const currentDay = d.getDay();
            const daysToAdd = (slot.weekday + 7 - currentDay) % 7;
            d.setDate(d.getDate() + daysToAdd);
            const firstDateStr = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
            
            return {
              weekday: slot.weekday,
              start_time: `${slot.startTime}:00`,
              end_time: `${slot.endTime}:00`,
              first_date: firstDateStr
            };
          }),
        });
        if (!res.ok) throw new Error(res.error || t.createDrawer.error);
      } else {
        const res = await api.createAppointment({
          client_id: Number(clientId),
          employee_id: Number(employeeId),
          room_id: roomId,
          specialty_id: specialtyId,
          start_datetime: startDT,
          duration_minutes: duration,
          notes: notes ? notes.trim() : null,
          payment_amount_cents: isInsurance ? 0 : parseMoneyToCents(priceText),
          payment_status: isInsurance ? 'pending' : paymentStatus,
          insurance_provider_id: insuranceProviderId
        });
        if (!res.ok) throw new Error(res.error || t.createDrawer.error);
      }
      toast.success(t.createDrawer.success);
      onSuccess();
      onClose();
      setClientId(null);
      setNotes("");
      setIsRecurring(false);
    } catch (e: any) {
      toast.error(e.message || t.createDrawer.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="sm:max-w-2xl h-full flex flex-col p-0 border-l">
        <SheetHeader className="px-6 py-4 border-b flex flex-row items-center justify-between">
          <SheetTitle className="text-xl font-bold">{t.createDrawer.title}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-white">
          <ClientProfessionalSection
            t={t}
            lang={lang}
            clients={clients}
            professionals={professionals}
            specialties={specialties}
            insuranceOptions={insuranceOptions}
            clientId={clientId}
            setClientId={setClientId}
            employeeId={employeeId}
            setEmployeeId={setEmployeeId}
            specialtyId={specialtyId}
            setSpecialtyId={setSpecialtyId}
            insuranceProviderId={insuranceProviderId}
            setInsuranceProviderId={setInsuranceProviderId}
            setRoomId={setRoomId}
          />

          <section className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-sm font-bold text-neutral-700 uppercase tracking-wider">{t.createDrawer.appointmentType}</Label>
              <div className="flex items-center gap-1 bg-neutral-100 p-1 rounded-lg border border-neutral-200 w-fit">
                <Button
                  type="button"
                  variant={!isRecurring ? "primary" : "ghost"}
                  size="sm"
                  className="h-8 text-[10px] px-4 font-bold uppercase tracking-wider transition-all"
                  onClick={() => setIsRecurring(false)}
                >
                  {t.createDrawer.typeSingle}
                </Button>
                <Button
                  type="button"
                  variant={isRecurring ? "primary" : "ghost"}
                  size="sm"
                  className="h-8 text-[10px] px-4 font-bold uppercase tracking-wider transition-all"
                  onClick={() => setIsRecurring(true)}
                >
                  {t.createDrawer.typeRecurring}
                </Button>
              </div>
            </div>
          </section>

          {!isRecurring ? (
            <DateTimeSection
              t={t}
              lang={lang}
              date={date}
              setDate={setDate}
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
            />
          ) : (
            <RecurrenceSection
              t={t}
              recurringSlots={recurringSlots}
              addSlot={addSlot}
              removeSlot={removeSlot}
              updateSlot={updateSlot}
              endType={endType}
              setEndType={setEndType}
              recurrenceWeeks={recurrenceWeeks}
              setRecurrenceWeeks={setRecurrenceWeeks}
            />
          )}

          <PaymentSection
            t={t}
            lang={lang}
            isInsurance={isInsurance}
            isRecurring={isRecurring}
            priceText={priceText}
            setPriceText={setPriceText}
            paymentStatus={paymentStatus}
            setPaymentStatus={setPaymentStatus}
            billingDay={billingDay}
            setBillingDay={setBillingDay}
            isProportional={isProportional}
            setIsProportional={setIsProportional}
            proportionalType={proportionalType}
            setProportionalType={setProportionalType}
            firstPaymentDate={firstPaymentDate}
            setFirstPaymentDate={setFirstPaymentDate}
            proportionalPercentage={proportionalPercentage}
            setProportionalPercentage={setProportionalPercentage}
            firstPaymentText={firstPaymentText}
            setFirstPaymentText={setFirstPaymentText}
          />

          <NotesSection
            t={t}
            notes={notes}
            setNotes={setNotes}
          />
        </div>

        <div className="p-6 border-t bg-white flex items-center justify-end gap-3 mt-auto">
          <Button variant="outline" onClick={onClose} className="h-10 px-6">
            {t.common.cancel}
          </Button>
          <Button onClick={handleSave} disabled={!canSave || loading} className="h-10 px-8">
            {loading ? t.common.loading : t.common.save}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
